/**
 * Срез референсов обозревателя библиотек (чистая логика, без React): вердикт → генерация → тег → метки,
 * счётчики у фишек и группы автотегов. Мемоизация — в компоненте (`useMemo` над этими функциями):
 * ссылка на `shown` держит якорь Shift в `useSelection`.
 */
import type { Marks } from "../../../lib/autotag";
import { byMarks, groupMissing } from "../../../lib/autotag.ts";
import type { ReferenceItem, Verdict } from "../../../lib/library";
import type { Tag } from "../../../lib/tags";
import type { UsedFilter } from "../../../lib/usedFilter";
import { byUsed } from "../../../lib/usedFilter.ts";

export type VerdictFilter = Verdict | "none" | "all";
/** Фильтр тега: тег, «без тега», «без тега группы» (`g<id>`) или ничего. */
export type TagFilter = number | "none" | `g${number}` | null;

export type AutoGroup = { id: number; name: string; tagIds: number[] };

/** Сколько референсов несут каждый тег. */
export function countTags(items: readonly ReferenceItem[]): Map<number, number> {
  const out = new Map<number, number>();
  for (const i of items) for (const t of i.tag_ids) out.set(t, (out.get(t) ?? 0) + 1);
  return out;
}

/** Группы с автотегами: фишка «‹Группа›: без тега N» — фото без единого тега группы (их отсёк порог). */
export function autoGroups(tags: readonly Tag[]): AutoGroup[] {
  const out = new Map<number, AutoGroup>();
  for (const t of tags) {
    if (t.group_id === null) continue;
    const g = out.get(t.group_id) ?? { id: t.group_id, name: t.group_name ?? `группа ${t.group_id}`, tagIds: [] };
    g.tagIds.push(t.id);
    out.set(t.group_id, g);
  }
  const auto = new Set(tags.filter((t) => t.prompt_en !== null && t.group_id !== null).map((t) => t.group_id));
  return [...out.values()].filter((g) => auto.has(g.id)).sort((a, b) => a.name.localeCompare(b.name, "ru"));
}

/** Шаг 1: вердикт (только на экране библиотеки). */
export function byVerdict<T extends ReferenceItem>(items: T[], filterable: boolean, verdictFilter: VerdictFilter): T[] {
  if (!filterable || verdictFilter === "all") return items;
  return items.filter((i) => (verdictFilter === "none" ? i.verdict === null : i.verdict === verdictFilter));
}

/** Шаг 2: «генерация: все | была | не было» (число у фишки тега считается по этому срезу). */
export function beforeTagSlice<T extends ReferenceItem>(byVerdictItems: T[], filterable: boolean, usedFilter: UsedFilter): T[] {
  return filterable ? byUsed(byVerdictItems, usedFilter) : byVerdictItems;
}

/** Шаг 3: тег / «без тега» / «без тега группы» / метки выбранного тега. */
export function sliceItems<T extends ReferenceItem>(
  beforeTag: T[],
  filterable: boolean,
  tagFilter: TagFilter,
  marks: Marks,
  filterGroup: { tagIds: number[] } | null,
): T[] {
  if (!filterable || tagFilter === null) return beforeTag;
  if (tagFilter === "none") return beforeTag.filter((i) => i.tag_ids.length === 0);
  if (typeof tagFilter === "number") return byMarks(beforeTag, tagFilter, marks);
  return filterGroup ? groupMissing(beforeTag, filterGroup.tagIds) : beforeTag;
}

/** Числа у фишек вердикта — по всем строкам экрана. */
export function verdictCounts(items: readonly ReferenceItem[]): { all: number; works: number; fails: number; none: number } {
  return {
    all: items.length,
    works: items.filter((i) => i.verdict === "works").length,
    fails: items.filter((i) => i.verdict === "fails").length,
    none: items.filter((i) => i.verdict === null).length,
  };
}
